import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Layout/Sidebar";
import Header from "../../components/Layout/Header";
import Toast from "../../components/Common/Toast";
import commonService from "../../services/otherServices";
import { useNavigate } from "react-router-dom";
import { formatIST } from "../../utils/dateHelpers";
import "../../assets/styles/dashboard.css";

import {
  Activity,
  CheckCircle2,
  Clock3,
  AlertTriangle,
  Mail,
  Users,
  Layers3,
  RefreshCcw,
  CalendarRange,
  TrendingUp
} from "lucide-react";


const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState<any>(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // =========================================================
  // FETCH DASHBOARD
  // =========================================================

  const fetchDashboard = async () => {

    try {

      setLoading(true);

      const res = await commonService.dashboard({
        start_date: startDate,
        end_date: endDate
      });

      setDashboard(res.data);

    } catch (error) {

      setToast({
        type: "error",
        message: "Failed to load dashboard"
      });

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  // =========================================================
  // METRIC CARD
  // =========================================================

  const MetricCard = ({
    title,
    value,
    icon,
    color,
    subtitle,
    click,
  }: any) => {

    return (
      <div onClick={click}
        className="metric-card"
        style={{
          cursor: click ? "pointer" : "default",
        }}
      >

        <div
          style={{
            position: "absolute",
            top: "-25px",
            right: "-25px",
            width: "90px",
            height: "90px",
            background: color,
            opacity: 0.08,
            borderRadius: "50%"
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >

          <div>

            <div className="metric-card-title">
              {title}
            </div>

            <div className="metric-card-value">
              {value}
            </div>

            <div className="metric-card-subtitle">
              {subtitle}
            </div>
          </div>

          <div
            style={{
              width: "58px",
              height: "58px",
              borderRadius: "16px",
              background: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white"
            }}
          >
            {icon}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="app-container">

      <Sidebar />

      <div className="main">

        <Header />

        <div
          className="content dashboard-content"
        >

          {/* ===================================================== */}
          {/* HERO SECTION */}
          {/* ===================================================== */}

          <div
            style={{
              background:
                "linear-gradient(135deg, #477edc 0%, #477edc 100%)",
              borderRadius: "24px",
              padding: "30px",
              color: "white",
              marginBottom: "25px",
              boxShadow: "0 15px 40px rgba(79,70,229,0.2)"
            }}
          >

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "20px"
              }}
            >

              <div>

                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: 800,
                    marginBottom: "8px"
                  }}
                >
                  Campaign Dashboard
                </div>

                <div
                  style={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "15px"
                  }}
                >
                  Track campaigns, leads, email validation and performance.
                </div>
              </div>

              {/* FILTERS */}

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap"
                }}
              >

                <div style={filterBoxStyle} >
                  <CalendarRange size={16} />

                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) =>
                      setStartDate(e.target.value)
                    }
                    style={filterInputStyle}
                  />
                </div>

                <div style={filterBoxStyle} >
                  <CalendarRange size={16} />

                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) =>
                      setEndDate(e.target.value)
                    }
                    style={filterInputStyle}
                  />
                </div>

                <button
                  onClick={fetchDashboard}
                  style={refreshBtnStyle}
                >
                  <RefreshCcw size={16} />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* ===================================================== */}
          {/* LOADING */}
          {/* ===================================================== */}

          {
            loading ? (
              <div className="dashboard-loading">
                Loading dashboard...
              </div>
            ) : (
              <>

                {/* ===================================================== */}
                {/* TOP STATS */}
                {/* ===================================================== */}

                <div className="dashboard-stats-grid">

                  <MetricCard
                    title="Total Campaigns"
                    value={
                      dashboard?.campaign_summary?.total_campaigns || 0
                    }
                    subtitle="Overall campaigns"
                    color="#4f46e5"
                    icon={<Layers3 size={26} />}
                    click={() => navigate("/campaigns")}
                  />

                  <MetricCard
                    title="Completed"
                    value={
                      dashboard?.campaign_summary?.completed || 0
                    }
                    subtitle="Successfully completed"
                    color="#16a34a"
                    icon={<CheckCircle2 size={26} />}
                  />

                  <MetricCard
                    title="Pending"
                    value={
                      dashboard?.campaign_summary?.pending || 0
                    }
                    subtitle="Awaiting actions"
                    color="#f59e0b"
                    icon={<Clock3 size={26} />}
                  />

                  <MetricCard
                    title="In Progress"
                    value={
                      dashboard?.campaign_summary?.inprogress || 0
                    }
                    subtitle="Currently processing"
                    color="#2563eb"
                    icon={<Activity size={26} />}
                  />
                  <MetricCard
                    title="Ready Campaigns"
                    value={
                      dashboard?.campaign_summary?.ready || 0
                    }
                    subtitle="Ready for launch"
                    color="#7c3aed"
                    icon={<TrendingUp size={26} />}
                  />
                  {/* <MetricCard
                    title="Total Leads"
                    value={
                      dashboard?.lead_summary?.total_leads || 0
                    }
                    subtitle="Generated leads"
                    color="#0f766e"
                    icon={<Users size={26} />}
                  /> */}

                </div>

                {/* ===================================================== */}
                {/* SECOND ROW */}
                {/* ===================================================== */}

                {/* <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "20px",
                    marginBottom: "25px"
                  }}
                >



                  <MetricCard
                    title="Total Emails"
                    value={
                      dashboard?.email_summary?.total_emails || 0
                    }
                    color="#000000"
                    icon={<Mail size={26} />}
                  />
                  <MetricCard
                    title="Valid Emails"
                    value={
                      dashboard?.email_summary?.valid_emails || 0
                    }
                    color="#059669"
                    icon={<Mail size={26} />}
                  />
                  <MetricCard
                    title="Invalid Emails"
                    value={
                      dashboard?.email_summary?.invalid_emails || 0
                    }
                    color="#dc2626"
                    icon={<Mail size={26} />}
                  />
                  <MetricCard
                    title="Catch-All Emails"
                    value={
                      dashboard?.email_summary?.catch_all_emails || 0
                    }
                    color="#729605"
                    icon={<Mail size={26} />}
                  />

                  <MetricCard
                    title="Risky Emails"
                    value={
                      dashboard?.email_summary?.risky_emails || 0
                    }
                    color="#eb9a9a"
                    icon={<Mail size={26} />}
                  />

                </div> */}

                {/* ===================================================== */}
                {/* CONTENT GRID */}
                {/* ===================================================== */}

                <div className="dashboard-main-grid">

                  {/* ================================================= */}
                  {/* USER PERFORMANCE */}
                  {/* ================================================= */}

                  <div className="dashboard-panel">

                    <div className="dashboard-panel-title">
                      Team Performance
                    </div>

                    {
                      dashboard?.user_summary?.length === 0 ? (
                        <div>No users found</div>
                      ) : (
                        dashboard?.user_summary?.map(
                          (user: any, index: number) => (
                            <div
                              key={index}
                              className="team-row"
                            >

                              <div>

                                <div className="team-name">
                                  {user.created_by}
                                </div>

                                <div className="team-sub">
                                  {user.lead_count} leads generated
                                </div>
                              </div>

                              <div className="team-badge">
                                {user.campaign_count}
                              </div>
                            </div>
                          )
                        )
                      )
                    }
                  </div>

                  {/* ================================================= */}
                  {/* RECENT CAMPAIGNS */}
                  {/* ================================================= */}

                  <div className="dashboard-panel">

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px"
                      }}
                    >

                      <div className="dashboard-panel-title">
                        Recent Campaigns
                      </div>

                      <div className="dashboard-panel-meta">
                        Latest activities
                      </div>
                    </div>

                    <div
                      style={{
                        overflowX: "auto",
                        WebkitOverflowScrolling: "touch"
                      }}
                    >

                      <table
                        style={{
                          width: "100%",
                          minWidth: "560px",
                          borderCollapse: "collapse"
                        }}
                      >

                        <thead>
                          <tr>
                            <th className="dashboard-th">Campaign</th>
                            <th className="dashboard-th">Status</th>
                            <th className="dashboard-th">Leads</th>
                            <th className="dashboard-th">Created By</th>
                            <th className="dashboard-th">Created</th>
                          </tr>
                        </thead>

                        <tbody>

                          {
                            dashboard?.recent_campaigns?.map(
                              (item: any) => (
                                <tr
                                  key={item.id}
                                  style={{
                                    transition:
                                      "all 0.2s ease"
                                  }}
                                >

                                  <td className="dashboard-td">
                                    <div className="dashboard-td-strong">
                                      {item.file_name}
                                    </div>
                                  </td>

                                  <td className="dashboard-td">

                                    <span
                                      style={{
                                        padding: "6px 12px",
                                        borderRadius: "999px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        textTransform: "uppercase",

                                        background:
                                          item.status === "ready"
                                            ? "#dcfce7"
                                            : item.status === "open"
                                              ? "#fee2e2"
                                              : item.status === "completed"
                                                ? "#d1fae5"
                                                : "#dbeafe",

                                        color:
                                          item.status === "ready"
                                            ? "#166534"
                                            : item.status === "open"
                                              ? "#991b1b"
                                              : item.status === "completed"
                                                ? "#065f46"
                                                : "#1d4ed8"
                                      }}
                                    >
                                      {item.status}
                                    </span>
                                  </td>

                                  <td className="dashboard-td">
                                    {item.data_count}
                                  </td>

                                  <td className="dashboard-td">
                                    {item.created_by}
                                  </td>

                                  <td className="dashboard-td">
                                    {
                                      formatIST(item.created_at, { day: "2-digit", month: "2-digit", year: "numeric" })
                                    }
                                  </td>
                                </tr>
                              )
                            )
                          }

                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )
          }
        </div>
      </div>

      {/* ===================================================== */}
      {/* TOAST */}
      {/* ===================================================== */}

      {
        toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )
      }
    </div>
  );
};

// =========================================================
// STYLES
// =========================================================

const filterBoxStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.15)",
  padding: "10px 14px",
  borderRadius: "12px",
  color: "white"
};

const filterInputStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  outline: "none",
  color: "white",
  fontSize: "14px"
};

const refreshBtnStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  background: "white",
  color: "#4f46e5",
  border: "none",
  borderRadius: "12px",
  padding: "12px 18px",
  fontWeight: 700,
  cursor: "pointer"
};

export default DashboardPage;
